import {
	createLegacyAdminUploadMigrationSource,
	db,
	getAllChunksMeta,
	getChunkBuffer,
	readFileUploadSession,
	type FileUploadMigrationChunk,
	type FileUploadSession
} from "@/db/fileUploadDb";
import {
	createAdminUploadMigrationFenceGate,
	createDefaultAdminUploadMigrationFenceStore,
	AdminUploadMigrationBlockedError
} from "./adminUploadMigration";

/**
 * Production P-11 bridge admission for Admin uploads.
 *
 * Admin's existing file-upload stores are already the shared engine's concrete durable stores. The
 * bridge therefore STAMPS AND FENCES ROWS IN PLACE: there is no second store to copy bytes into, and
 * deleting the source would delete the adapter's only durable copy. What a pass does per idle row is
 * (1) confirm the row and its chunks are exactly what we just read, and (2) CAS the engine-identity
 * stamps onto it. Nothing is written twice and nothing is deleted.
 */

/**
 * True when the durable row still matches what the pass read a moment ago. Deliberately stricter than
 * the adoption CAS itself: the CAS only guards owner/adoption fields, while a torn chunk store means
 * this row must not be stamped as an adopted, resumable upload at all.
 */
async function isRowUnchanged(session: FileUploadSession, expectedChunks: FileUploadMigrationChunk[]): Promise<boolean> {
	const current = await readFileUploadSession(session.id);
	if (!current || current.uploadId !== session.uploadId || current.conversationId !== session.conversationId) return false;
	const chunks = await getAllChunksMeta(session.id);
	if (chunks.length !== expectedChunks.length) return false;
	for (const expected of expectedChunks) {
		const persisted = chunks.find((chunk) => chunk.chunkIndex === expected.meta.chunkIndex);
		if (!persisted || persisted.size !== expected.meta.size || persisted.isUploaded !== expected.meta.isUploaded) return false;
		if (expected.meta.isUploaded === 0) {
			const data = await getChunkBuffer(session.id, expected.meta.chunkIndex);
			if (!data || data.byteLength !== expected.meta.size) return false;
		}
	}
	return true;
}

/** The stamps markAdopted writes. A row already carrying the marker must carry ALL of them. */
function assertAdoptedStampsAreComplete(session: FileUploadSession): void {
	if (
		session.uploadAdoptionState !== "adopted" ||
		session.uploadEngineFamily !== "legacy" ||
		!Number.isSafeInteger(session.uploadSchemaVersion) ||
		!Number.isSafeInteger(session.uploadAdapterContractVersion)
	) {
		throw new AdminUploadMigrationBlockedError(`Upload ${session.id} is marked adopted with incomplete identity stamps.`);
	}
}

export interface AdminUploadAdoptionPassResult {
	adopted: number;
	skippedLive: number;
	alreadyAdopted: number;
}

/**
 * Classify a lost `markAdopted` CAS from the durable row. Adopted by the other tab -> the row is
 * already bridged (its stamps must be complete, exactly as for a row found adopted at listing time);
 * anything else -> the other writer owns it now, so treat it like a row that was live at the
 * free-check and leave it for the drain. Both branches log the identifiers a multi-tab incident
 * needs, because the pre-fix failure mode here was silent file loss.
 */
async function recordLostAdoptionRace(sessionId: string, result: AdminUploadAdoptionPassResult): Promise<void> {
	const current = await readFileUploadSession(sessionId);
	if (current?.uploadAdoptionState === "adopted") {
		assertAdoptedStampsAreComplete(current);
		result.alreadyAdopted++;
		console.warn("[Upload] Legacy upload row was adopted by another tab during this tab's bridge pass; continuing", { sessionId });
		return;
	}
	result.skippedLive++;
	console.warn("[Upload] Legacy upload row was claimed by another writer during this tab's bridge pass; left for the drain", {
		sessionId,
		rowPresent: current !== undefined,
		uploadOwnerId: current?.uploadOwnerId ?? null,
		uploadOwnerClaimId: current?.uploadOwnerClaimId ?? null
	});
}

/** One in-place adoption pass over every idle legacy row. Live rows are left for the drain to reclaim. */
async function adoptIdleLegacyRows(): Promise<AdminUploadAdoptionPassResult> {
	const source = createLegacyAdminUploadMigrationSource();
	const result: AdminUploadAdoptionPassResult = { adopted: 0, skippedLive: 0, alreadyAdopted: 0 };

	for (const listedSession of await source.listSessions()) {
		const sessionId = listedSession.id;
		if (typeof sessionId !== "string" || sessionId.length === 0) {
			throw new AdminUploadMigrationBlockedError("Legacy upload row has no stable id");
		}
		const session = await source.getSession(sessionId);
		if (!session) continue;
		if ((await source.getLeaseState(session)) !== "free") {
			result.skippedLive++;
			continue;
		}
		const writerLease = await source.acquireWriterLease?.(session);
		if (source.acquireWriterLease && !writerLease) {
			result.skippedLive++;
			continue;
		}
		try {
			const chunks = await source.getChunks(session);
			if (!(await isRowUnchanged(session, chunks))) {
				throw new AdminUploadMigrationBlockedError(`Upload ${sessionId} changed while preparing the bridge; source retained.`);
			}
			const wasMarkedAdopted = listedSession.uploadAdoptionState === "adopted";
			if (wasMarkedAdopted) {
				assertAdoptedStampsAreComplete(session);
				result.alreadyAdopted++;
			} else if (!(await source.markAdopted(sessionId, writerLease))) {
				// LSP-19599: markAdopted is a CAS and a lost race is BENIGN — another tab of the same
				// browser adopted this row, or claimed it as its live writer, between this tab's
				// free-check and this stamp. This pass gates EVERY upload writer on the page through one
				// memoized promise, so throwing here failed unrelated in-flight pre-persists (silent
				// multi-tab file loss). Record the outcome and move on; the torn-chunk-store check above
				// remains the only blocking condition.
				await recordLostAdoptionRace(sessionId, result);
				continue;
			}
			result.adopted++;
		} finally {
			await writerLease?.release();
		}
	}
	return result;
}

let bridgeReady: Promise<void> | undefined;
const productionFenceStore = createDefaultAdminUploadMigrationFenceStore();

/**
 * Idempotently publish/read the bridge fence and adopt all idle rows before an
 * Admin writer is admitted. Failure is deliberately surfaced: falling back to
 * an unfenced writer would permit old and new bundle paths to mutate one row.
 */
export function ensureAdminUploadBridgeReady(): Promise<void> {
	if (!bridgeReady) {
		bridgeReady = (async () => {
			const gate = createAdminUploadMigrationFenceGate({ fence: productionFenceStore });
			await gate.prepareBridge();
			await gate.readFence();
			const result = await adoptIdleLegacyRows();
			await gate.recordAdoption(result.adopted > 0);
		})().catch((error) => {
			bridgeReady = undefined;
			throw error;
		});
	}
	return bridgeReady;
}

/** Test-only reset; production intentionally keeps one bridge admission promise per page. */
export function resetAdminUploadBridgeForTests(): void {
	bridgeReady = undefined;
}
